import { IBaseEntity } from "../core/contracts/IBaseEntity";
import { ContentType } from "./Contracts";

export class BaseEntityRecordBinding {
    private _record: IBaseEntity;
    private _original: IBaseEntity;
    public entity(): EntityBinding<IBaseEntity> {
        return {
            entityName: "BaseEntity",
            pluralName: "Base Entities",
            recordName: "Base Entity",
            singularName: "Base Entity",
            relations: BaseEntityRecordBinding.getBaseRelations(),
            columns: BaseEntityRecordBinding.getBaseColumnBinding(),
            labelsMap: new Map<string, string>(),
        };
    }
    constructor(record: IBaseEntity) {
        this._record = record;
        this._original = { ...record };
    }

    protected revert() {
        this._record = this._original;
    }
    public static getBaseColumnBinding() {
        return {
            id: BaseEntityRecordBinding._idBaseColumnBinding,
            createdByUserId: BaseEntityRecordBinding._createdByUserIdBaseColumnBinding,
            createdTime: BaseEntityRecordBinding._createdTimeBaseColumnBinding,
            owner: BaseEntityRecordBinding._ownerBaseColumnBinding,
            lastModifiedByUserId: BaseEntityRecordBinding._lastModifiedByUserIdBaseColumnBinding,
            lastModifiedTime: BaseEntityRecordBinding._lastModifiedTimeBaseColumnBinding,
            name: BaseEntityRecordBinding._nameBaseColumnBinding,
        };
    }
    public static getBaseRelations() {
        return {
            owner: "USERS_",
            lastModifiedByUserId: "USERS_",
            createdByUserId: "USERS_",
        };
    }
    protected static _idBaseColumnBinding: BaseColumnBinding = {
        fieldName: "id",
        isRequired: true,
        contentType: "ID",
        dbColumnName: "ID",
        label: "Id",
        options: null,
        relatedObject: null,
    };
    protected _idColumnBinding: ColumnBinding<number> = {
        ...BaseEntityRecordBinding._idBaseColumnBinding,
    };
    public get id(): ColumnBinding<number> {
        return this._idColumnBinding;
    }
    protected static _nameBaseColumnBinding: BaseColumnBinding = {
        fieldName: "name",
        isRequired: false,
        contentType: "TEXT",
        dbColumnName: "NAME",
        label: "Name",
        options: null,
        relatedObject: null,
    };
    protected _nameColumnBinding: ColumnBinding<string | null> = {
        ...BaseEntityRecordBinding._nameBaseColumnBinding,
        fieldName: this.entity().recordName,
    };
    public get name(): ColumnBinding<string | null> {
        return this._nameColumnBinding;
    }
    public get owner(): ColumnBinding<number> {
        return this._ownerColumnBinding;
    }
    protected static _ownerBaseColumnBinding: BaseColumnBinding = {
        fieldName: "owner",
        isRequired: true,
        contentType: "LOOKUP",
        dbColumnName: "OWNER",
        relatedObject: "USERS_",
        label: "Owner",
        options: null,
    };
    protected _ownerColumnBinding: ColumnBinding<number> = {
        ...BaseEntityRecordBinding._ownerBaseColumnBinding,
    };
    protected static _createdTimeBaseColumnBinding: BaseColumnBinding = {
        fieldName: "createdTime",
        isRequired: true,
        contentType: "DATETIME",
        dbColumnName: "CREATED_TIME",
        label: "Created Time",
        options: null,
        relatedObject: null,
    };
    protected _createdTimeColumnBinding: ColumnBinding<Date> = {
        ...BaseEntityRecordBinding._createdTimeBaseColumnBinding,
    };
    public get createdTime(): ColumnBinding<Date> {
        return this._createdTimeColumnBinding;
    }
    protected static _lastModifiedTimeBaseColumnBinding: BaseColumnBinding = {
        fieldName: "lastModifiedTime",
        isRequired: true,
        contentType: "DATETIME",
        dbColumnName: "LAST_MODIFIED_TIME",
        label: "Last Modified Time",
        options: null,
        relatedObject: null,
    };
    protected _lastModifiedTimeColumnBinding: ColumnBinding<Date> = {
        ...BaseEntityRecordBinding._lastModifiedTimeBaseColumnBinding,
    };
    public get lastModifiedTime(): ColumnBinding<Date> {
        return this._lastModifiedTimeColumnBinding;
    }
    protected static _lastModifiedByUserIdBaseColumnBinding: BaseColumnBinding = {
        fieldName: "lastModifiedByUserId",
        isRequired: true,
        contentType: "LOOKUP",
        dbColumnName: "LAST_MODIFIED_BY_USER_ID",
        relatedObject: "USERS_",
        label: "Last Modified By User Id",
        options: null,
    };
    protected _lastModifiedByUserIdColumnBinding: ColumnBinding<number> = {
        ...BaseEntityRecordBinding._lastModifiedByUserIdBaseColumnBinding,
    };
    public get lastModifiedByUserId(): ColumnBinding<number> {
        return this._lastModifiedByUserIdColumnBinding;
    }
    protected static _createdByUserIdBaseColumnBinding: BaseColumnBinding = {
        fieldName: "createdByUserId",
        isRequired: true,
        contentType: "LOOKUP",
        dbColumnName: "CREATED_BY_USER_ID",
        relatedObject: "USERS_",
        label: "Created By User Id",
        options: null,
    };
    protected _createdByUserIdColumnBinding: ColumnBinding<number> = {
        ...BaseEntityRecordBinding._createdByUserIdBaseColumnBinding,
    };
    public get createdByUserId(): ColumnBinding<number> {
        return this._createdByUserIdColumnBinding;
    }

    public isValid(binding: ColumnBinding<any>): boolean {
        const value = "" as any;
        if (binding.isRequired && (typeof value === "undefined" || value === null || value === "")) {
            return false;
        }
        if (binding.contentType == "EMAIL" || binding.contentType == "TEXT" || binding.contentType == "PHONE") {
            if (binding.length) {
                if (value) {
                    return value.toString().length <= 255;
                } else {
                    return true;
                }
            }
        }
        return true;
    }

    protected get baseColumns() {
        return [
            this._idColumnBinding,
            this._nameColumnBinding,
            this._createdByUserIdColumnBinding,
            this._createdTimeColumnBinding,
            this._lastModifiedByUserIdColumnBinding,
            this._lastModifiedTimeColumnBinding,
            this._ownerColumnBinding,
        ];
    }
}
export interface BaseColumnBinding {
    label: string;
    fieldName: string;
    dbColumnName: string;
    contentType: ContentType;
    isRequired: boolean;
    length?: number | undefined;
    relatedObject: string | null;
    relationName?: string | undefined;
    defaultValue?: string | null;
    helpText?: string | null;
    options?: string[] | null;
    hideForTemplate?: boolean;
    templateLabel?: string;
    minValue?: number | null;
    maxValue?: number | null;
    roundTo?: number | null;
    unique?: boolean | null;
    picklistName?: string | null;
    schemaColumnName?: string | null;
    schemaRelationColumn?: string | null;
}

export interface ColumnBinding<T> extends BaseColumnBinding {
    getValue?: () => T;
    setValue?: (newValue: any) => void;
    isValid?: () => boolean;
    getRelatedRecord?: () => IBaseEntity | null;
}

export interface EntityBinding<T> {
    entityName: string;
    singularName: string;
    pluralName: string;
    recordName: string;
    relations: Relations;
    columns: EntityBindingColumns<T>;
    labelsMap: Map<string, string>;
    entityClass?: string;
}

export type EntityBindingColumns<T> = {
    [K in keyof T]: BaseColumnBinding;
};

export type Relations = {
    [columnName: string]: string;
};
export interface Relation {
    relatedEntityName: string;
    relatedColumnName: string;
}
